import { useState, useEffect } from 'preact/hooks';
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api';
import { WorkflowRule, type WorkflowRuleData, type WorkflowExecution } from '../components/WorkflowRule';

type FormMode = 'list' | 'create' | 'edit';

const CONDITION_TYPES = [
  { value: 'service_down', label: 'Service Down (Gatus)' },
  { value: 'security_alert', label: 'Security Alert' },
  { value: 'disk_threshold', label: 'Disk-Schwellwert' },
  { value: 'manual', label: 'Manuell' },
];

const ACTION_TYPES = [
  { value: 'restart', label: 'Service Restart' },
  { value: 'telegram_message', label: 'Telegram-Nachricht' },
  { value: 'escalation', label: 'Eskalation' },
];

interface RuleForm {
  id?: string;
  name: string;
  condition_type: string;
  condition_config: string;
  action_type: string;
  action_config: string;
  is_active: boolean;
}

const emptyForm: RuleForm = {
  name: '',
  condition_type: 'service_down',
  condition_config: '{}',
  action_type: 'telegram_message',
  action_config: '{}',
  is_active: true,
};

export function Workflows() {
  const [rules, setRules] = useState<WorkflowRuleData[]>([]);
  const [executionsMap, setExecutionsMap] = useState<Record<string, WorkflowExecution[]>>({});
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<FormMode>('list');
  const [form, setForm] = useState<RuleForm>(emptyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRules();
  }, []);

  async function loadRules() {
    try {
      setLoading(true);
      const data = await apiGet<WorkflowRuleData[]>('/workflows/rules');
      setRules(data);

      // Load history for each rule
      const histMap: Record<string, WorkflowExecution[]> = {};
      await Promise.all(
        data.map(async (rule) => {
          try {
            const hist = await apiGet<WorkflowExecution[]>(`/workflows/history?ruleId=${rule.id}`);
            histMap[rule.id] = hist;
          } catch {
            histMap[rule.id] = [];
          }
        })
      );
      setExecutionsMap(histMap);
    } catch {
      setError('Workflow-Regeln konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }

  function handleCreate() {
    setForm(emptyForm);
    setMode('create');
    setError('');
  }

  function handleEdit(rule: WorkflowRuleData) {
    setForm({
      id: rule.id,
      name: rule.name,
      condition_type: rule.condition_type,
      condition_config: JSON.stringify(rule.condition_config, null, 2),
      action_type: rule.action_type,
      action_config: JSON.stringify(rule.action_config, null, 2),
      is_active: !!rule.is_active,
    });
    setMode('edit');
    setError('');
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setError('Name ist erforderlich.');
      return;
    }

    let condConfig: Record<string, unknown>;
    let actConfig: Record<string, unknown>;
    try {
      condConfig = JSON.parse(form.condition_config);
    } catch {
      setError('Condition-Config ist kein gültiges JSON.');
      return;
    }
    try {
      actConfig = JSON.parse(form.action_config);
    } catch {
      setError('Action-Config ist kein gültiges JSON.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        id: form.id,
        name: form.name.trim(),
        condition_type: form.condition_type,
        condition_config: condConfig,
        action_type: form.action_type,
        action_config: actConfig,
        is_active: form.is_active,
      };

      if (mode === 'create') {
        await apiPost('/workflows/rules', payload);
        setSuccess('Regel erstellt.');
      } else {
        await apiPut('/workflows/rules', payload);
        setSuccess('Regel aktualisiert.');
      }

      setMode('list');
      await loadRules();
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(id: string, active: boolean) {
    const rule = rules.find((r) => r.id === id);
    if (!rule) return;

    try {
      await apiPut('/workflows/rules', {
        id: rule.id,
        name: rule.name,
        condition_type: rule.condition_type,
        condition_config: rule.condition_config,
        action_type: rule.action_type,
        action_config: rule.action_config,
        is_active: active,
      });
      setRules((prev) =>
        prev.map((r) => (r.id === id ? { ...r, is_active: active ? 1 : 0 } : r))
      );
    } catch {
      setError('Status-Änderung fehlgeschlagen.');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Regel wirklich löschen?')) return;
    try {
      await apiDelete(`/workflows/rules?id=${id}`);
      setRules((prev) => prev.filter((r) => r.id !== id));
      setSuccess('Regel gelöscht.');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Löschen fehlgeschlagen.');
    }
  }

  if (loading) {
    return (
      <div class="page">
        <h1>Workflows</h1>
        <p>Laden...</p>
      </div>
    );
  }

  if (mode !== 'list') {
    return (
      <div class="page">
        <h1>{mode === 'create' ? 'Neue Regel' : 'Regel bearbeiten'}</h1>

        {error && <div class="alert alert-error">{error}</div>}

        <div class="workflow-form">
          <div class="form-group">
            <label class="form-label">Name</label>
            <input
              class="form-input"
              value={form.name}
              onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })}
              placeholder="z.B. Traefik-Down → Restart"
            />
          </div>

          <div class="form-group">
            <label class="form-label">Bedingung</label>
            <select
              class="form-select"
              value={form.condition_type}
              onChange={(e) => setForm({ ...form, condition_type: (e.target as HTMLSelectElement).value })}
            >
              {CONDITION_TYPES.map((ct) => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Bedingung-Config (JSON)</label>
            <textarea
              class="form-textarea"
              value={form.condition_config}
              onInput={(e) => setForm({ ...form, condition_config: (e.target as HTMLTextAreaElement).value })}
              rows={3}
              placeholder='{"service_id": "bm-traefik"}'
            />
          </div>

          <div class="form-group">
            <label class="form-label">Aktion</label>
            <select
              class="form-select"
              value={form.action_type}
              onChange={(e) => setForm({ ...form, action_type: (e.target as HTMLSelectElement).value })}
            >
              {ACTION_TYPES.map((at) => (
                <option key={at.value} value={at.value}>{at.label}</option>
              ))}
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Aktion-Config (JSON)</label>
            <textarea
              class="form-textarea"
              value={form.action_config}
              onInput={(e) => setForm({ ...form, action_config: (e.target as HTMLTextAreaElement).value })}
              rows={3}
              placeholder='{"message": "Service ist down!"}'
            />
          </div>

          <div class="form-group">
            <label class="toggle-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: (e.target as HTMLInputElement).checked })}
              />
              Regel aktiv
            </label>
          </div>

          <div class="form-actions">
            <button class="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Speichern...' : 'Speichern'}
            </button>
            <button class="btn btn-secondary" onClick={() => setMode('list')} disabled={saving}>
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="page">
      <div class="page-header">
        <h1>Workflows</h1>
        <button class="btn btn-primary" onClick={handleCreate}>+ Neue Regel</button>
      </div>

      {error && <div class="alert alert-error">{error}</div>}
      {success && <div class="alert alert-success">{success}</div>}

      {rules.length === 0 ? (
        <p class="empty-hint">Keine Workflow-Regeln konfiguriert.</p>
      ) : (
        <div class="workflow-list">
          {rules.map((rule) => (
            <WorkflowRule
              key={rule.id}
              rule={rule}
              executions={executionsMap[rule.id] ?? []}
              onToggleActive={handleToggleActive}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
